import { inject } from '@angular/core';
import { CanActivateFn, CanMatchFn, Router, UrlTree } from '@angular/router';
import { CollaborateurService } from '@shared/services/cabinet/collaborateur.service';
import { DossierAccessRight } from '@shared/models/dossier-extended-dto.model';
import { ClientDossiersService } from './client-dossiers.service';
import { clientGuard } from './guards/client.guard';

/*
 * Client avec « accès complet » sur un dossier (develop seulement) : idée de Claire du 23/10, « accès collaborateur =
 * accès client final avec accès complet ». Sur un tel dossier, le client voit le menu du collaborateur (menu double,
 * layout/cadre-dossier/dossier-collab.ts) et ses écrans, limités à ceux dont le back accepte les lectures avec un
 * jeton client (relevé le 23/10 sur le 118 avec claire.bagou@logaia.fr, voir TODO-back.md).
 * Décision de Claire (24/10) : accès complet = mode collaborateur tel quel (menu, libellés, vue professionnelle, actions
 * d'écriture) ; il garde en plus l'accueil du client (menu Accueil › Accueil / Centralisateur), l'en-tête et le titre de
 * l'onglet du client.
 * N'est importé que par du code placé derrière LOGAIA_COLLAB_BUILD (routes collaborateur, menu double) : absent du
 * build « client seul » (production, préproduction), où le client avec accès complet garde le menu client.
 */

/**
 * Écrans collaborateur (chemin relatif au dossier, sous-chemins compris) que le back refuse à un client avec accès
 * complet, ou qui exigent un collaborateur : ni dans son menu ni ouverts par adresse (refus habituel puis accueil).
 * Relevé du 23/10 (118, jeton client) : toutes les lectures des autres écrans collaborateur répondent 200 (fiche
 * société et ses onglets, saisie standard, révision, rapprochement, emprunts, immobilisations, crédit-bail, états
 * financiers, recherche d'écriture) ; les écritures n'ont pas été essayées.
 */
const ECRANS_COLLAB_FERMES_AU_CLIENT: readonly string[] = [
  // Archives : le mode archive (?dateArchive=, en-têtes d'archive) est une fonction du collaborateur.
  'archives',
  // Dossier annuel : saisie autorisée selon le login du collaborateur (créateur ou autorisés du travail en cours) ;
  // le travail et la situation ne sont lus que pour un collaborateur (core/chargement-dossier.resolver.ts).
  'dossier-de-travail',
];

/**
 * Écrans du client ouverts aussi au collaborateur (Suivi des clients, Suivi des fournisseurs : décision de Claire du
 * 24/10, mêmes écrans que chez le client) : collaborateur, sinon la garde du client (clientGuard). À écrire derrière
 * LOGAIA_COLLAB_BUILD dans les routes (build « client seul » : clientGuard seul).
 */
export const ecranClientOuvertAuCollabGuard: CanActivateFn = (route, state) =>
  inject(CollaborateurService).isCollab || clientGuard(route, state);

/** Accès complet du client sur ce dossier. */
export function clientAccesComplet(access: DossierAccessRight | undefined): boolean {
  return !!access?.accesComplet;
}

/** Écran collaborateur (chemin relatif au dossier) ouvert au client avec accès complet. */
export function ecranOuvertAccesComplet(chemin: string): boolean {
  return !ECRANS_COLLAB_FERMES_AU_CLIENT.some((c) => chemin === c || chemin.startsWith(c + '/'));
}

/** Id du dossier et chemin de l'écran (relatif au dossier) d'une adresse « /dossier/42/… » ; null sinon. */
export function ecranDeLAdresse(url: UrlTree): { dossierId: number; chemin: string } | null {
  const segments = url.root.children['primary']?.segments.map((s) => s.path) ?? [];
  const dossierId = segments[0] === 'dossier' ? Number(segments[1]) : 0;
  return dossierId ? { dossierId, chemin: segments.slice(2).join('/') } : null;
}

/**
 * Route d'un écran collaborateur (canMatch) : collaborateur, ou client avec accès complet sur le dossier de l'adresse
 * demandée si l'écran lui est ouvert ; sinon la route suivante de même chemin est essayée (refus au client).
 * Les gardes passent avant le resolver : le dossier et l'écran sont lus dans l'adresse de la navigation en cours.
 */
export const vueCollabGuard: CanMatchFn = () => {
  const collaborateurService = inject(CollaborateurService);
  if (collaborateurService.isCollab) {
    return true;
  }
  const navigation = inject(Router).currentNavigation();
  const ecran = navigation ? ecranDeLAdresse(navigation.finalUrl ?? navigation.extractedUrl) : null;
  return (
    !!ecran &&
    collaborateurService.isClient &&
    clientAccesComplet(inject(ClientDossiersService).accesPour(ecran.dossierId)) &&
    ecranOuvertAccesComplet(ecran.chemin)
  );
};
